/**
 * StuckReframe — user reports a difficulty / new thought / blocker on a card.
 *
 * 2026-05-21 v3:
 *   - Chips reduced 5 → 4: removed "先放一放" because it's not a *strategy
 *     adjustment*. If the user wants to set the card aside, they pick "继续做"
 *     in TraceInput; reframe is for cards the user still wants, just done
 *     differently. The four remaining chips are all real adjustment directions.
 *   - Waiting UI redesigned to match WishInput's deck-stream wait — same
 *     WaitingMascotArena + typewriter narration, so reframe and deck feel like
 *     two sides of the same product. No more overlay+spinner.
 *
 * Server still diagnoses 7 classes (emotional_state still reachable when users
 * type free-form things like "我不想做了") and responds in rewrite/clarify/exit.
 */
import { useEffect, useRef, useState } from 'react';
import type { PoolCard } from '../state/cardPool';
import { apiPost, ApiError } from '../api/client';
import { WaitingMascotArena } from '../components/WaitingMascotArena';
import type { ReframeMode, ReframeDiagnosis } from '@gime5/shared';

const ADJUST_CHIPS = [
  '第一步太大,想再小一点',
  '第一步太轻,想加点料',
  '方向不对,我想做的不是这件事',
  '情况对不上,现在做不了这一步',
];

const WAITING_LINES = [
  '在想哪里可以再小一点…',
  '重新拆这一步…',
  '换一个角度看这张卡…',
  '把行动写成下一步能直接接上的样子…',
  '快好了…',
];
const VALUE_PROP_LINES = [
  '把这张卡换成下一步能碰到的样子。',
  '不硬拧,先找到最小的入口。',
  '把模糊的阻力翻成一个可执行动作。',
  '保留目标,换一条更能启动的路。',
  '让这一步小到现在就能开始。',
];
const WAITING_LINE_INTERVAL_MS = 1500;

interface StuckReframeProps {
  card: PoolCard;
  onAccepted: (newAction: string) => void;
  /** User chose "放一放" — keep the card in pool with current action, return to ThreeCards. */
  onPutAside: () => void;
  onCancel: () => void;
}

interface ReframeApiResponse {
  ok: boolean;
  adapter: string;
  data: {
    mode: ReframeMode;
    diagnosis: ReframeDiagnosis;
    newAction?: string;
    question?: string;
    message?: string;
  };
}

type ResponsePayload = ReframeApiResponse['data'];

export function StuckReframe({ card, onAccepted, onPutAside, onCancel }: StuckReframeProps) {
  const [feedback, setFeedback] = useState('');
  const [pending, setPending] = useState(false);
  const [response, setResponse] = useState<ResponsePayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  // clarify mode: user types a follow-up reply
  const [clarifyReply, setClarifyReply] = useState('');
  // Waiting-overlay narration rotates while pending. No minimum dwell — if the API
  // returns before the first line cycles, the overlay vanishes immediately.
  const [waitingIdx, setWaitingIdx] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!pending) {
      setWaitingIdx(0);
      return;
    }
    const id = window.setInterval(() => {
      setWaitingIdx((i) => (i + 1) % WAITING_LINES.length);
    }, WAITING_LINE_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [pending]);

  async function submit(text: string) {
    const trimmed = text.trim();
    if (!trimmed || pending) return;
    setPending(true);
    setError(null);
    try {
      const data = await apiPost<ReframeApiResponse>('/api/agent/reframe', {
        cardId: card.id,
        cardTitle: card.title,
        oldAction: card.action,
        actionHistory: card.actionHistory,
        stuckReason: trimmed,
      });
      setResponse(data.data);
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError('这次没拼出来,稍等再试一次就好。');
    } finally {
      setPending(false);
    }
  }

  // 2026-05-20 v2: chip click now writes the phrase into textarea (appending with a
  // newline if there's existing content). User can edit / supplement before submitting.
  function onChipClick(chipText: string) {
    setFeedback((prev) => {
      const trimmed = prev.trim();
      return trimmed ? `${trimmed}\n${chipText}` : chipText;
    });
    // focus + move cursor to end after React commits the new value
    window.requestAnimationFrame(() => {
      const ta = textareaRef.current;
      if (ta) {
        ta.focus();
        const end = ta.value.length;
        ta.setSelectionRange(end, end);
      }
    });
  }

  function onTextSubmit(e: React.FormEvent) {
    e.preventDefault();
    void submit(feedback);
  }

  function onClarifyResubmit() {
    const reply = clarifyReply.trim();
    if (!reply) return;
    const combined = `${feedback}\n\n补充:${reply}`;
    setResponse(null);
    setClarifyReply('');
    void submit(combined);
  }

  // ---- pending (same vibe as WishInput's thinking-panel for visual continuity) ----
  if (pending) {
    return (
      <section className="panel wish thinking-panel">
        <span className="tiny-label">
          <i className="dot cyan pulse" /> 正在调整这一步
        </span>
        <h1>「{card.title}」</h1>
        <div
          className="value-prop-rotator"
          onCopy={(e) => e.preventDefault()}
          onCut={(e) => e.preventDefault()}
          onContextMenu={(e) => e.preventDefault()}
          onDragStart={(e) => e.preventDefault()}
        >
          <WaitingMascotArena />
          <p className="value-prop" key={waitingIdx}>
            {VALUE_PROP_LINES[waitingIdx % VALUE_PROP_LINES.length]}
          </p>
        </div>
        <p className="thinking-line">
          <span className="thinking-prompt">›</span>
          <span className="thinking-text">{WAITING_LINES[waitingIdx]}</span>
          <span className="thinking-caret" aria-hidden="true" />
        </p>
      </section>
    );
  }

  // ---- rewrite ----
  if (response?.mode === 'rewrite' && response.newAction) {
    return (
      <section className="panel wish stuck-reframe">
        <span className="tiny-label">
          <i className="dot lime" /> 新的行动
        </span>
        <h1>「{card.title}」</h1>
        <div className="proposed-strategy panel artifact">
          <p className="strategy-new">{response.newAction}</p>
          <div className="actions">
            <button
              className="btn-lime"
              type="button"
              onClick={() => onAccepted(response.newAction!)}
            >
              用这个行动
            </button>
            <button
              className="btn-ghost"
              type="button"
              onClick={() => setResponse(null)}
            >
              再换一个
            </button>
            <button className="link-subtle" type="button" onClick={onCancel}>
              回工作区
            </button>
          </div>
        </div>
      </section>
    );
  }

  // ---- clarify ----
  if (response?.mode === 'clarify' && response.question) {
    return (
      <section className="panel wish stuck-reframe">
        <span className="tiny-label">
          <i className="dot cyan" /> 还差一点信息
        </span>
        <h1>「{card.title}」</h1>
        <p className="clarify-question">{response.question}</p>
        <textarea
          className="text-input textarea"
          value={clarifyReply}
          onChange={(e) => setClarifyReply(e.target.value)}
          rows={3}
          placeholder="一两句话就够。"
          autoFocus
          disabled={pending}
          maxLength={500}
        />
        <div className="actions actions-between">
          <button className="link-subtle" type="button" onClick={onCancel}>
            ← 算了,回工作区
          </button>
          <button
            className="btn-lime"
            type="button"
            onClick={onClarifyResubmit}
            disabled={pending || !clarifyReply.trim()}
          >
            {pending ? '正在调整...' : '提交补充'}
          </button>
        </div>
        {error && (
          <p className="error-line">
            <i className="dot amber" /> {error}
          </p>
        )}
      </section>
    );
  }

  // ---- exit ----
  if (response?.mode === 'exit' && response.message) {
    return (
      <section className="panel wish stuck-reframe">
        <span className="tiny-label">
          <i className="dot amber" /> 这张卡的去处
        </span>
        <h1>「{card.title}」</h1>
        <p className="exit-message">{response.message}</p>
        <div className="actions">
          <button className="btn-lime" type="button" onClick={onPutAside}>
            好,放一放
          </button>
          <button
            className="btn-ghost"
            type="button"
            onClick={() => setResponse(null)}
          >
            不,我再写一下
          </button>
        </div>
      </section>
    );
  }

  // ---- input ----
  return (
    <section className="panel wish stuck-reframe">
      <span className="tiny-label">
        <i className="dot cyan" /> 想调一下这张
      </span>
      <h1>「{card.title}」</h1>
      <div className="strategy-row">
        <span className="tiny muted">现在的行动:</span>
        <p className="strategy-current">{card.action}</p>
      </div>

      <form onSubmit={onTextSubmit}>
        <span className="tiny-label">
          <i className="dot" /> 想做哪种调整(点一个填到下面,再补一两句自己的话)
        </span>
        <div className="adjust-chips">
          {ADJUST_CHIPS.map((chip) => (
            <button
              key={chip}
              type="button"
              className="adjust-chip"
              onClick={() => onChipClick(chip)}
              disabled={pending}
            >
              {chip}
            </button>
          ))}
        </div>

        <textarea
          ref={textareaRef}
          className="text-input textarea"
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          rows={4}
          placeholder="点一个上面的胶囊,或者直接自己写一两句。"
          disabled={pending}
          maxLength={500}
        />

        <div className="actions actions-between">
          <button className="link-subtle" type="button" onClick={onCancel}>
            ← 回工作区
          </button>
          <button
            className="btn-lime"
            type="submit"
            disabled={pending || !feedback.trim()}
          >
            提交
          </button>
        </div>
      </form>

      {error && (
        <p className="error-line">
          <i className="dot amber" /> {error}
        </p>
      )}
    </section>
  );
}
